/**
 * IconButton - Simple, robust button component with icon and automatic padding
 * Factory function following the app's architectural patterns
 */

import { createIcon, type IconAPI } from "@UI/Icon/index.js";
import { isPrimaryTouchDevice } from "@utils/device.js";

export type IconButtonBehavior = "button" | "switch" | "momentary";
export type IconButtonSize = 24 | 28 | 30 | 32 | 44;
export type IconSize = 24 | 28;
export type IconButtonPointerType = "mouse" | "touch" | "pen";
export type IconButtonPressHandler = (selected?: boolean) => void;
export type IconButtonDoubleHandler = (event: MouseEvent | PointerEvent) => void;
export type IconButtonLongHandler = (event: PointerEvent) => void;

export interface IconButtonConfig {
  readonly icon: string;
  readonly size?: IconButtonSize; // Button container size (24, 28, 44)
  readonly iconSize?: IconSize; // Icon size (24, 28)
  readonly rounded?: boolean; // Background color
  readonly transparentBg?: boolean; // Background color
  readonly behavior?: IconButtonBehavior; // 'button', 'switch', or 'momentary'
  readonly selected?: boolean; // Initial selected state
  readonly disabled?: boolean; // Disabled state
  readonly switchIcon?: string; // Icon for switch selected state
  readonly className?: string; // Additional CSS classes
  readonly onClick?: IconButtonPressHandler; // Click handler
  readonly onDoubleClick?: (event: MouseEvent) => void;
  readonly onPress?: IconButtonPressHandler;
  readonly onDoublePress?: IconButtonDoubleHandler;
  readonly onLongPress?: IconButtonLongHandler;
  readonly doublePressDelayMs?: number;
  readonly longPressDelayMs?: number;
  readonly longPressPointerTypes?: readonly IconButtonPointerType[];
  readonly suppressSingleOnDouble?: boolean;
  // Pointer event handlers for momentary behavior
  readonly onPointerDown?: (event: PointerEvent) => void;
  readonly onPointerUp?: (event: PointerEvent) => void;
}

export interface IconButtonAPI {
  readonly getElement: () => HTMLElement;
  readonly setSelected: (selected: boolean) => void;
  readonly getSelected: () => boolean;
  readonly setDisabled: (disabled: boolean) => void;
  readonly getDisabled: () => boolean;
  readonly setIcon: (iconName: string) => void;
  readonly triggerPrimary: () => void;
  readonly destroy: () => void;
}

/**
 * Creates an IconButton with automatic padding and state management
 */
export const createIconButton = (config: IconButtonConfig): IconButtonAPI => {
  const {
    icon,
    size = 44,
    iconSize = 24,
    rounded = true,
    transparentBg = true,
    behavior = "button",
    selected = false,
    disabled = false,
    switchIcon,
    className = "",
    onClick,
    onDoubleClick,
    onPress,
    onDoublePress,
    onLongPress,
    doublePressDelayMs = 300,
    longPressDelayMs = 520,
    longPressPointerTypes = ["touch", "pen"],
    suppressSingleOnDouble = true,
    onPointerDown,
    onPointerUp,
  } = config;

  // Validate size combinations
  if (iconSize > size) {
    console.warn(
      `IconButton: iconSize (${iconSize}) cannot be larger than size (${size})`
    );
  }

  // State management
  let isSelected = selected;
  let isDisabled = disabled;
  let isDestroyed = false;
  let lastPressTimestamp = 0;
  let pendingSingleTimer: number | null = null;
  let suppressClickUntil = 0;
  let longPressTimer: number | null = null;
  let longPressFired = false;
  let lastLongPressAt = 0;
  let activePointerId: number | null = null;
  let activePointerType: IconButtonPointerType | null = null;

  const primaryHandler = onPress ?? onClick;
  const doublePressHandler = onDoublePress;
  const doubleClickHandler = onDoubleClick;

  const firePrimary = (): void => {
    if (behavior === "switch") {
      isSelected = !isSelected;
      updateState();
      primaryHandler?.(isSelected);
      return;
    }
    primaryHandler?.(behavior === "switch" ? isSelected : undefined);
  };

  const cancelPendingSingle = (): void => {
    if (pendingSingleTimer !== null) {
      window.clearTimeout(pendingSingleTimer);
      pendingSingleTimer = null;
    }
    lastPressTimestamp = 0;
  };

  const handlePress = (event: MouseEvent | PointerEvent): void => {
    if (!primaryHandler && !doublePressHandler && !doubleClickHandler) return;
    if (!doublePressHandler && !doubleClickHandler) {
      firePrimary();
      return;
    }
    if (!suppressSingleOnDouble) {
      const now = performance.now?.() ?? Date.now();
      if (now - lastPressTimestamp <= doublePressDelayMs) {
        lastPressTimestamp = 0;
        if (doublePressHandler) {
          doublePressHandler(event);
        } else if (doubleClickHandler) {
          doubleClickHandler(event as MouseEvent);
        }
        return;
      }
      lastPressTimestamp = now;
      firePrimary();
      return;
    }
    const now = performance.now?.() ?? Date.now();
    if (now - lastPressTimestamp <= doublePressDelayMs) {
      cancelPendingSingle();
      if (doublePressHandler) {
        doublePressHandler(event);
      } else if (doubleClickHandler) {
        doubleClickHandler(event as MouseEvent);
      }
      return;
    }
    lastPressTimestamp = now;
    pendingSingleTimer = window.setTimeout(() => {
      pendingSingleTimer = null;
      lastPressTimestamp = 0;
      firePrimary();
    }, doublePressDelayMs);
  };

  const clearLongPressTimer = (): void => {
    if (longPressTimer !== null) {
      window.clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  };

  const startLongPressTimer = (event: PointerEvent): void => {
    if (!onLongPress) return;
    if (!longPressPointerTypes.includes(event.pointerType as IconButtonPointerType)) {
      return;
    }
    clearLongPressTimer();
    longPressFired = false;
    longPressTimer = window.setTimeout(() => {
      longPressTimer = null;
      longPressFired = true;
      lastLongPressAt = performance.now?.() ?? Date.now();
      suppressClickUntil = lastLongPressAt + doublePressDelayMs + 100;
      cancelPendingSingle();
      onLongPress(event);
    }, longPressDelayMs);
  };

  // Create button element
  const button = document.createElement("button");
  button.type = "button";

  // Detect touch device and add appropriate class
  const isTouchDevice = isPrimaryTouchDevice();
  const touchClass = isTouchDevice ? "touch-device" : "";
  button.className =
    `icon-button icon-button--${size} ${touchClass} ${className} ${
      rounded ? "icon-button--rounded" : ""
    } ${transparentBg ? "icon-button--transparent-bg" : ""}`.trim();

  // Create icon
  let iconAPI: IconAPI;
  try {
    iconAPI = createIcon({
      name: icon,
      size: iconSize,
      useContainer: true,
    });
  } catch (error) {
    console.error(`Failed to create icon "${icon}" for IconButton:`, error);
    throw error;
  }

  // Append icon to button
  button.appendChild(iconAPI.getElement());

  /**
   * Update button state and appearance
   */
  const updateState = (): void => {
    if (isDestroyed) return;

    // Update CSS classes
    button.classList.toggle("icon-button--selected", isSelected);
    button.classList.toggle("icon-button--disabled", isDisabled);

    // Update disabled attribute
    button.disabled = isDisabled;

    // Update icon for switch behavior
    if (behavior === "switch" && switchIcon) {
      const currentIcon = isSelected ? switchIcon : icon;
      iconAPI.setIcon(currentIcon);
    }
  };

  /**
   * Handle button click
   */
  const handleClick = (event: MouseEvent): void => {
    if (isDisabled || isDestroyed) return;
    const now = performance.now?.() ?? Date.now();
    if (now < suppressClickUntil) return;
    if (longPressFired || now - lastLongPressAt < 500) return;
    handlePress(event);
  };

  const handlePointerDown = (event: PointerEvent): void => {
    if (isDisabled || isDestroyed) return;
    activePointerId = event.pointerId;
    activePointerType = event.pointerType as IconButtonPointerType;
    startLongPressTimer(event);
    onPointerDown?.(event);
  };

  const handlePointerUp = (event: PointerEvent): void => {
    if (isDisabled || isDestroyed) return;
    if (activePointerId !== null && event.pointerId !== activePointerId) return;
    clearLongPressTimer();
    if (longPressFired) {
      longPressFired = false;
      activePointerId = null;
      activePointerType = null;
      return;
    }
    onPointerUp?.(event);
    if (event.pointerType !== "mouse") {
      suppressClickUntil =
        (performance.now?.() ?? Date.now()) + doublePressDelayMs + 50;
      handlePress(event);
    }
    activePointerId = null;
    activePointerType = null;
  };

  const handlePointerCancel = (event: PointerEvent): void => {
    if (activePointerId !== null && event.pointerId !== activePointerId) return;
    clearLongPressTimer();
    longPressFired = false;
    activePointerId = null;
    activePointerType = null;
  };

  let handleTouchStart: ((event: TouchEvent) => void) | null = null;
  let handleTouchEnd: (() => void) | null = null;
  let handleTouchCancel: (() => void) | null = null;

  // Set up event listeners
  if (behavior === "momentary") {
    // For momentary behavior, use pointer events instead of click
    // Track if button is currently being pressed
    let isCurrentlyPressed = false;

    const handleDown = (event: PointerEvent) => {
      if (isDisabled || isDestroyed) return;
      // Prevent double-activation if already pressed by another pointer
      if (isCurrentlyPressed) return;
      
      // NOTE: Do NOT use setPointerCapture here!
      // On iPad Safari, having pointer capture active on one element
      // can block Apple Pencil events from reaching other elements.
      // Instead, we rely on pointerup/pointerleave to detect release.
      
      isSelected = true;
      isCurrentlyPressed = true;
      updateState();

      activePointerId = event.pointerId;
      activePointerType = event.pointerType as IconButtonPointerType;
      startLongPressTimer(event);
      
      onPointerDown?.(event);
    };

    const handleUp = (event: PointerEvent) => {
      if (isDisabled || isDestroyed) return;
      if (!isCurrentlyPressed) return;
      if (activePointerId !== null && event.pointerId !== activePointerId) return;
      
      isSelected = false;
      isCurrentlyPressed = false;
      updateState();

      clearLongPressTimer();
      longPressFired = false;
      activePointerId = null;
      activePointerType = null;
      
      onPointerUp?.(event);
    };

    // Use non-passive listeners
    const listenerOptions: AddEventListenerOptions = { passive: false };
    
    button.addEventListener("pointerdown", handleDown, listenerOptions);
    button.addEventListener("pointerup", handleUp, listenerOptions);
    button.addEventListener("pointercancel", handleUp, listenerOptions);
    // Also handle pointerleave in case finger slides off button
    button.addEventListener("pointerleave", handleUp, listenerOptions);
    
    // Touch event handlers as fallback for iPad Safari
    button.addEventListener("touchstart", (event: TouchEvent) => {
      if (isDisabled || isDestroyed || isCurrentlyPressed) return;
      // Don't preventDefault or stopPropagation - let other touches work
      isSelected = true;
      isCurrentlyPressed = true;
      updateState();
      if (event.touches[0]) {
        const touch = event.touches[0];
        const syntheticEvent = new PointerEvent("pointerdown", {
          pointerId: touch.identifier,
          clientX: touch.clientX,
          clientY: touch.clientY,
          pointerType: "touch",
        });
        startLongPressTimer(syntheticEvent);
        onPointerDown?.(syntheticEvent);
      }
    }, listenerOptions);
    
    button.addEventListener("touchend", (event: TouchEvent) => {
      if (isDisabled || isDestroyed || !isCurrentlyPressed) return;
      isSelected = false;
      isCurrentlyPressed = false;
      updateState();
      clearLongPressTimer();
      longPressFired = false;
      if (onPointerUp) {
        const syntheticEvent = new PointerEvent("pointerup", {
          pointerType: "touch",
        });
        onPointerUp(syntheticEvent);
      }
    }, listenerOptions);
    
    button.addEventListener("touchcancel", (event: TouchEvent) => {
      if (isDisabled || isDestroyed || !isCurrentlyPressed) return;
      isSelected = false;
      isCurrentlyPressed = false;
      updateState();
      clearLongPressTimer();
      longPressFired = false;
      if (onPointerUp) {
        const syntheticEvent = new PointerEvent("pointerup", {
          pointerType: "touch",
        });
        onPointerUp(syntheticEvent);
      }
    }, listenerOptions);

    // Only prevent context menu when button is actually being pressed
    button.addEventListener("contextmenu", (event: Event) => {
      if (isCurrentlyPressed) {
        event.preventDefault();
      }
      // Let context menu work normally when button is not being pressed
    });
  } else {
    // For button and switch behavior, use click + pointer events
    button.addEventListener("click", handleClick);
    button.addEventListener("pointerdown", handlePointerDown);
    button.addEventListener("pointerup", handlePointerUp);
    button.addEventListener("pointercancel", handlePointerCancel);
    button.addEventListener("pointerleave", handlePointerCancel);
    const supportsPointerEvents =
      typeof window !== "undefined" && "PointerEvent" in window;
    if (!supportsPointerEvents) {
      handleTouchStart = (event: TouchEvent) => {
        if (isDisabled || isDestroyed || !event.touches[0]) return;
        const touch = event.touches[0];
        const syntheticEvent = new PointerEvent("pointerdown", {
          pointerId: touch.identifier,
          clientX: touch.clientX,
          clientY: touch.clientY,
          pointerType: "touch",
        });
        handlePointerDown(syntheticEvent);
      };
      handleTouchEnd = (): void => {
        if (isDisabled || isDestroyed) return;
        const syntheticEvent = new PointerEvent("pointerup", {
          pointerId: activePointerId ?? 0,
          pointerType: "touch",
        });
        handlePointerUp(syntheticEvent);
      };
      handleTouchCancel = (): void => {
        if (isDisabled || isDestroyed) return;
        const syntheticEvent = new PointerEvent("pointercancel", {
          pointerId: activePointerId ?? 0,
          pointerType: "touch",
        });
        handlePointerCancel(syntheticEvent);
      };
      button.addEventListener("touchstart", handleTouchStart, {
        passive: false,
      });
      button.addEventListener("touchend", handleTouchEnd, { passive: false });
      button.addEventListener("touchcancel", handleTouchCancel, {
        passive: false,
      });
    }
  }

  // Apply initial state
  updateState();

  // Public API
  const api: IconButtonAPI = {
    getElement: () => button,

    setSelected: (newSelected: boolean) => {
      if (isDestroyed) return;
      isSelected = newSelected;
      updateState();
    },

    getSelected: () => isSelected,

    setDisabled: (newDisabled: boolean) => {
      if (isDestroyed) return;
      isDisabled = newDisabled;
      updateState();
    },

    getDisabled: () => isDisabled,

    setIcon: (iconName: string) => {
      if (isDestroyed) return;
      iconAPI.setIcon(iconName);
    },

    triggerPrimary: () => {
      if (isDestroyed || isDisabled) return;
      cancelPendingSingle();
      firePrimary();
    },

    destroy: () => {
      if (isDestroyed) return;
      isDestroyed = true;

      button.removeEventListener("click", handleClick);
      button.removeEventListener("pointerdown", handlePointerDown);
      button.removeEventListener("pointerup", handlePointerUp);
      button.removeEventListener("pointercancel", handlePointerCancel);
      button.removeEventListener("pointerleave", handlePointerCancel);
      if (handleTouchStart) {
        button.removeEventListener("touchstart", handleTouchStart);
      }
      if (handleTouchEnd) {
        button.removeEventListener("touchend", handleTouchEnd);
      }
      if (handleTouchCancel) {
        button.removeEventListener("touchcancel", handleTouchCancel);
      }
      clearLongPressTimer();
      cancelPendingSingle();
      iconAPI.destroy();
      button.remove();
    },
  };

  return api;
};

/**
 * Utility function to create and append IconButton to container
 */
export const createIconButtonInContainer = (
  container: HTMLElement,
  config: IconButtonConfig
): IconButtonAPI => {
  const iconButton = createIconButton(config);
  container.appendChild(iconButton.getElement());
  return iconButton;
};
